import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { User, UserRole } from "@/shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { Plus, UserCog, Pencil, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient } from "@/lib/queryClient";
import * as usersService from "@/services/users";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";

export default function UsersPage() {
    const { hasPermission, user: currentUser } = useAuth() as any;
    const { toast } = useToast();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

    const { data: users, isLoading } = useQuery<User[]>({
        queryKey: ["/api/users"],
        queryFn: async () => await usersService.listUsers(),
        onError: (error: Error) => {
            toast({
                title: "Failed to load users",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const canWrite = hasPermission("users", "write");

    const createMutation = useMutation({
        mutationFn: async (data: any) => {
            return await usersService.createUser(data as any);
        },
        onSuccess: (resBody: any) => {
            queryClient.invalidateQueries({ queryKey: ["/api/users"] });
            const created = resBody?.user || resBody?.data || resBody?.result || (Array.isArray(resBody) ? resBody[0] : null);
            const label = created ? (created.name || created.email) : null;
            toast({ title: label ? `User created: ${label}` : "User created successfully" });
            setDialogOpen(false);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to create user",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<User> }) => {
            return await usersService.updateUser(id, data);
        },
        onSuccess: (resBody: any) => {
            queryClient.invalidateQueries({ queryKey: ["/api/users"] });
            const updated = resBody?.user || resBody?.data || resBody?.result || null;
            const label = updated ? (updated.name || updated.email) : null;
            toast({ title: label ? `User updated: ${label}` : "User updated successfully" });
            setDialogOpen(false);
            setEditingUser(null);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to update user",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (payload: { id: string } | string) => {
            const id = typeof payload === 'string' ? payload : payload.id;
            return await usersService.deleteUser(id);
        },
        onSuccess: (_resBody, variables) => {
            queryClient.invalidateQueries({ queryKey: ["/api/users"] });
            // variables may be the id string or an object with id and label
            let label: string | null = null;
            if (typeof variables === 'string') label = variables;
            else if (variables && typeof variables === 'object') label = (variables as any).label || (variables as any).id || null;
            toast({ title: label ? `User deleted: ${label}` : `User deleted` });
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to delete user",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const toggleActiveMutation = useMutation({
        mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
            return await usersService.updateUser(id, { isActive } as any);
        },
        // Optimistic update: immediately update cache, rollback on error
        onMutate: async ({ id, isActive }: { id: string; isActive: boolean }) => {
            await queryClient.cancelQueries({ queryKey: ["/api/users"] });
            const previous = queryClient.getQueryData<User[]>(["/api/users"]);
            queryClient.setQueryData<User[] | undefined>(["/api/users"], (old) => {
                if (!old) return old;
                return old.map((u) => (u.id === id ? { ...u, isActive } : u));
            });
            return { previous };
        },
        onError: (error: Error, variables, context: any) => {
            // rollback
            if (context?.previous) {
                queryClient.setQueryData(["/api/users"], context.previous);
            }
            toast({
                title: "Failed to update user status",
                description: error.message,
                variant: "destructive",
            });
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["/api/users"] });
            if (variables && typeof (variables as any).isActive === "boolean") {
                toast({ title: (variables as any).isActive ? "User activated" : "User deactivated" });
            } else {
                toast({ title: "User status updated" });
            }
        },
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const data = {
            email: formData.get("email") as string,
            name: formData.get("fullName") as string,
            role: formData.get("role") as UserRole,
            password: formData.get("password") as string,
        };

        if (editingUser) {
            const updateData: any = {
                email: data.email,
                name: data.name,
                role: data.role,
            };
            if (data.password) {
                updateData.password = data.password;
            }
            updateMutation.mutate({ id: editingUser.id, data: updateData });
        } else {
            createMutation.mutate(data);
        }
    };

    const getRoleColor = (role: string) => {
        switch (role) {
            case "owner":
                return "bg-chart-2/10 text-chart-2 border-chart-2/20";
            case "admin":
                return "bg-chart-1/10 text-chart-1 border-chart-1/20";
            case "hr":
                return "bg-chart-3/10 text-chart-3 border-chart-3/20";
            case "marketer":
                return "bg-chart-4/10 text-chart-4 border-chart-4/20";
            case "developer":
                return "bg-chart-5/10 text-chart-5 border-chart-5/20";
            case "sales":
                return "bg-primary/10 text-primary border-primary/20";
            case "intern":
                return "bg-muted/50 text-muted-foreground border-muted";
            default:
                return "";
        }
    };

    const getInitials = (name?: string) => {
        const src = (name || "").toString().trim();
        if (!src) return "";
        return src
            .split(/\s+/)
            .map((n) => (n && n[0] ? n[0] : ""))
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Users</h1>
                    <p className="text-muted-foreground mt-2">Manage user accounts and role assignments</p>
                </div>
                {canWrite && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => setEditingUser(null)} data-testid="button-create-user" disabled={createMutation.isPending}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add User
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>{editingUser ? "Edit User" : "Create New User"}</DialogTitle>
                                <DialogDescription>{editingUser ? "Update user information" : "Add a new user to the system"}</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email *</Label>
                                    <Input id="email" name="email" type="email" defaultValue={editingUser?.email} required data-testid="input-user-email" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="fullName">Full Name *</Label>
                                    <Input id="fullName" name="fullName" defaultValue={editingUser?.name} required data-testid="input-full-name" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="role">Role *</Label>
                                    <Select name="role" defaultValue={editingUser?.role || "intern"} required>
                                        <SelectTrigger data-testid="select-role">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="owner">Owner</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                            <SelectItem value="hr">HR</SelectItem>
                                            <SelectItem value="marketer">Marketer</SelectItem>
                                            <SelectItem value="developer">Developer</SelectItem>
                                            <SelectItem value="sales">Sales</SelectItem>
                                            <SelectItem value="intern">Intern</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">Password {editingUser && "(leave blank to keep current)"}</Label>
                                    <Input id="password" name="password" type="password" required={!editingUser} data-testid="input-user-password" />
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingUser(null); }}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-submit-user">
                                        {editingUser ? "Update" : "Create"} User
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <Card>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-6 space-y-4">{[1, 2, 3].map((i) => (<Skeleton key={i} className="h-16 w-full" />))}</div>
                    ) : users && users.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canWrite && <TableHead className="text-right">Actions</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Avatar className="w-10 h-10">
                                                    <AvatarFallback className="bg-primary/10 text-primary">{getInitials(user.name ?? user.email)}</AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-medium">{user.name}</p>
                                                    {user.id === (currentUser as any)?.id && (<Badge variant="outline" className="text-xs mt-1">You</Badge>)}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={getRoleColor(user.role)}>{user.role}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Switch checked={user.isActive} onCheckedChange={(checked) => { toggleActiveMutation.mutate({ id: user.id, isActive: checked }); }} disabled={!canWrite || user.id === (currentUser as any)?.id || toggleActiveMutation.isPending} data-testid={`switch-active-${user.id}`} />
                                                <span className="text-sm text-muted-foreground">{user.isActive ? "Active" : "Inactive"}</span>
                                            </div>
                                        </TableCell>
                                        {canWrite && (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button variant="ghost" size="icon" onClick={() => { setEditingUser(user); setDialogOpen(true); }} data-testid={`button-edit-user-${user.id}`} disabled={updateMutation.isPending}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setDeleteTarget(user);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                        disabled={user.id === (currentUser as any)?.id || deleteMutation.isPending}
                                                        data-testid={`button-delete-user-${user.id}`}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <UserCog className="h-12 w-12 text-muted-foreground/50 mb-4" />
                            <h3 className="text-lg font-semibold">No users yet</h3>
                            <p className="text-sm text-muted-foreground mt-2 max-w-sm">Add users to your organization and assign roles.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete user</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete <strong>{deleteTarget?.name || deleteTarget?.email}</strong>? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}>Cancel</Button>
                        <Button variant="destructive" onClick={() => {
                            if (deleteTarget) {
                                deleteMutation.mutate({ id: deleteTarget.id, label: deleteTarget.name || deleteTarget.email });
                            }
                            setDeleteDialogOpen(false);
                            setDeleteTarget(null);
                        }} disabled={deleteMutation.isPending} data-testid="confirm-delete-user">Delete</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
